import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import L from 'leaflet';
import Tangram from 'tangram';

import {
  useMapContext,
  useOverlayContext,
  useActiveHexContext,
} from 'shared/context';
import { useGrid } from 'shared/hooks';
import { MAP } from 'shared/constants';

import { CanvasGrid, CanvasHex } from 'containers';
import { GPSIcon } from 'components';

// @ts-ignore
import scene from 'shared/scene.yaml';

export const Map = (): JSX.Element => {
  const {
    lat,
    long,
    map,
    setMap,
    marker,
    setMarker,
    setTerrain,
    setSummary,
    setCoords,
  } = useMapContext();
  const { isBigHexSelectedRef, setIsBigHexViewActive } = useActiveHexContext();
  const { setLayer } = useOverlayContext();
  const [isMapInit, setIsMapInit] = useState<boolean>(false);
  const { handleGridChange } = useGrid();
  const mapRef = useRef(null);

  const initMap = async () => {
    if (!mapRef?.current) return;
    const newMap = L.map(mapRef.current);
    const newLayer = Tangram.leafletLayer({
      scene,
      events: { hover: onMapHover },
      interactive: true,
    });
    newLayer.addTo(newMap);
    newMap.setView([lat, long], MAP.ZOOM);
    setMap(newMap);
    setLayer(newLayer);
  };

  const initGrid = () => {
    const mapInitiated = map && mapRef?.current && !isMapInit;
    if (!mapInitiated) return;
    handleGridChange();
    map.on('move', handleGridChange); // this also triggers on zoom
    map.on('zoomend', handleZoomEnd);
    setIsMapInit(true);
  };

  const handleZoomEnd = () => {
    if (isBigHexSelectedRef.current) {
      setIsBigHexViewActive(true);
    }
  };

  const onMapHover = (selection: any) => {
    const feature = selection.feature;

    if (feature) {
      setSummary(feature.layers[0]);
      setTerrain(feature.properties.kind);
      setCoords(feature.tile.coords);
    }
  };

  useEffect(() => {
    if (map && mapRef?.current) {
      marker?.removeFrom(map);
      const newMarker = L.marker([lat, long], { icon: GPSIcon });
      newMarker.addTo(map);
      setMarker(newMarker);
    }
  }, [lat, long]);

  useEffect(() => {
    initGrid();
  }, [map, isMapInit]);

  useEffect(() => {
    initMap();
  }, []);

  return (
    <MapContainer ref={mapRef}>
      <CanvasHex />
      <CanvasGrid />
    </MapContainer>
  );
};

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
`;
